import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RichTextEditor } from '@/components/forms/RichTextEditor';
import { z } from 'zod';
import { PageHeader } from '@/components/common/PageHeader';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import { TextField } from '@/components/forms/TextField';
import { Select2 } from '@/components/forms/Select2';
import { Select2Field } from '@/components/forms/Select2Field';
import { Select2Multi } from '@/components/forms/Select2Multi';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { ApiError } from '@/api/client/abpError';
import {
  DeploymentNoticeStyle,
  MAX_NOTICE_TITLE_LENGTH,
} from '@/api/types/deploymentNotice';
import type { DeploymentNoticeTemplateDto } from '@/api/types/deploymentNotice';
import { DeploymentNoticePermissions } from './permissions';
import { SaveTemplateModal } from './SaveTemplateModal';
import {
  useClearNotice,
  useCreateTemplate,
  useDeleteTemplate,
  useDeployNotice,
  useNoticeStatus,
  useNoticeTenants,
  useStyleOptions,
  useTemplates,
} from './api';

interface NoticeFormValues {
  title: string;
  message: string;
  durationMinutes: number;
  style: string;
}

const schema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Please enter a title.')
    .max(MAX_NOTICE_TITLE_LENGTH, `Title must be at most ${MAX_NOTICE_TITLE_LENGTH} characters.`),
  message: z.string().trim().min(1, 'Please enter a message.'),
  durationMinutes: z.coerce.number().int().min(1, 'Duration must be at least 1 minute.'),
  style: z.string(),
});

function renderPreviewMessage(message: string, durationMinutes: number): string {
  const label = durationMinutes === 1 ? '1 minute' : `${durationMinutes} minutes`;
  return message.replace(/\{duration\}/g, `<span class="countdown">${label}</span>`);
}

export function DeploymentNoticePage() {
  const can = usePermission();
  const canManage = can(DeploymentNoticePermissions.manage);

  const toast = useToast();
  const statusQuery = useNoticeStatus();
  const styleQuery = useStyleOptions();
  const tenantsQuery = useNoticeTenants();
  const templatesQuery = useTemplates();

  const deployMut = useDeployNotice();
  const clearMut = useClearNotice();
  const createTemplateMut = useCreateTemplate();
  const deleteTemplateMut = useDeleteTemplate();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [targetTenantIds, setTargetTenantIds] = useState<string[]>([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [clearTarget, setClearTarget] = useState<string | null>(null);
  const [deleteTemplateTarget, setDeleteTemplateTarget] =
    useState<DeploymentNoticeTemplateDto | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    getValues,
    control,
    formState: { errors },
  } = useForm<NoticeFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '',
      message: '',
      durationMinutes: 5,
      style: String(DeploymentNoticeStyle.Warning),
    },
  });

  const watched = watch();
  const styleNum = Number(watched.style);
  const previewHtml = useMemo(
    () => renderPreviewMessage(watched.message || '', Number(watched.durationMinutes) || 0),
    [watched.message, watched.durationMinutes],
  );

  function reportError(err: unknown, fallback: string) {
    toast.error(err instanceof ApiError ? err.message : fallback);
  }

  function fillFromTemplate(id: string) {
    setSelectedTemplateId(id);
    const t = (templatesQuery.data ?? []).find((x) => x.id === id);
    if (t) {
      reset({
        title: t.title,
        message: t.message,
        durationMinutes: t.durationMinutes,
        style: String(t.style),
      });
    }
  }

  function onDeploy(values: NoticeFormValues) {
    deployMut.mutate(
      {
        title: values.title.trim(),
        message: values.message,
        durationMinutes: Number(values.durationMinutes),
        style: Number(values.style) as DeploymentNoticeStyle,
        templateId: selectedTemplateId || null,
        targetTenantIds: targetTenantIds.length > 0 ? targetTenantIds : null,
      },
      {
        onSuccess: () => toast.success('Notice deployed.'),
        onError: (err) => reportError(err, 'Failed to deploy the notice.'),
      },
    );
  }

  function handleSaveTemplate(name: string) {
    const values = getValues();
    createTemplateMut.mutate(
      {
        name,
        title: values.title.trim(),
        message: values.message,
        durationMinutes: Number(values.durationMinutes),
        style: Number(values.style) as DeploymentNoticeStyle,
      },
      {
        onSuccess: () => {
          toast.success('Template saved.');
          setShowSaveTemplate(false);
        },
        onError: (err) => reportError(err, 'Failed to save the template.'),
      },
    );
  }

  function confirmClear() {
    if (!clearTarget) return;
    clearMut.mutate(clearTarget, {
      onSuccess: () => {
        toast.success('Active notice cleared.');
        setClearTarget(null);
      },
      onError: (err) => {
        reportError(err, 'Failed to clear the notice.');
        setClearTarget(null);
      },
    });
  }

  function confirmDeleteTemplate() {
    if (!deleteTemplateTarget) return;
    deleteTemplateMut.mutate(deleteTemplateTarget.id, {
      onSuccess: () => {
        toast.success('Template deleted.');
        setDeleteTemplateTarget(null);
      },
      onError: (err) => {
        reportError(err, 'Failed to delete the template.');
        setDeleteTemplateTarget(null);
      },
    });
  }

  function clearInputs() {
    reset({
      title: '',
      message: '',
      durationMinutes: 5,
      style: String(DeploymentNoticeStyle.Warning),
    });
    setSelectedTemplateId('');
    setTargetTenantIds([]);
  }

  if (styleQuery.isLoading || tenantsQuery.isLoading) return <Spinner />;
  if (styleQuery.isError)
    return <ErrorState message="Could not load the page." onRetry={() => styleQuery.refetch()} />;

  const styleOptions = styleQuery.data ?? [];
  const tenants = tenantsQuery.data ?? [];
  const templates = templatesQuery.data ?? [];
  const activeNotices = statusQuery.data?.activeNotices ?? [];

  return (
    <div>
      <PageHeader title="Deployment Notice" breadcrumb={['Home', 'Deployment Notice']} />

      <div className="row g-3">
        <div className="col-lg-7">
          <div className="card custom-card">
            <div className="card-header">
              <h5 className="mb-0">Notice details</h5>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit(onDeploy)} noValidate>
                {templates.length > 0 && (
                  <Select2
                    className="mb-3"
                    label="Load from template"
                    placeholder="Select a template..."
                    value={selectedTemplateId}
                    onChange={(v) => fillFromTemplate(v ?? '')}
                    options={templates.map((t) => ({ value: t.id, label: t.name }))}
                  />
                )}

                <TextField
                  label="Title"
                  maxLength={MAX_NOTICE_TITLE_LENGTH}
                  error={errors.title?.message}
                  {...register('title')}
                />

                <div className="mb-3">
                  <label htmlFor="notice-message" className="form-label">
                    Message
                  </label>
                  <Controller
                    control={control}
                    name="message"
                    render={({ field }) => <RichTextEditor value={field.value ?? ''} onChange={field.onChange} placeholder="Use {duration} where the countdown should appear." />}
                  />
                  {errors.message && <div className="text-danger small mt-1">{errors.message.message}</div>}
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="notice-duration" className="form-label">
                      Duration (minutes)
                    </label>
                    <input
                      id="notice-duration"
                      type="number"
                      min={1}
                      className={`form-control${errors.durationMinutes ? ' is-invalid' : ''}`}
                      {...register('durationMinutes')}
                    />
                    {errors.durationMinutes && (
                      <div className="invalid-feedback">{errors.durationMinutes.message}</div>
                    )}
                  </div>
                  <div className="col-md-6">
                    <Select2Field
                      control={control}
                      name="style"
                      label="Style"
                      allowClear={false}
                      options={styleOptions.map((o) => ({ value: o.value, label: o.text }))}
                    />
                  </div>
                </div>

                {tenants.length > 0 && (
                  <div className="mb-3 mt-3">
                    <Select2Multi
                      label="Target tenants (none = all)"
                      value={targetTenantIds}
                      onChange={setTargetTenantIds}
                      options={tenants.map((t) => ({ value: t.id, label: t.name }))}
                      placeholder="All tenants"
                    />
                  </div>
                )}

                {canManage && (
                  <div className="d-flex gap-2 mt-3">
                    <button type="submit" className="btn btn-primary" disabled={deployMut.isPending}>
                      <i className="fa fa-bullhorn me-1" />
                      {deployMut.isPending ? 'Deploying…' : 'Deploy notice'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => setShowSaveTemplate(true)}
                    >
                      <i className="fa fa-save me-1" />
                      Save as template
                    </button>
                    <button type="button" className="btn btn-light" onClick={clearInputs}>
                      Clear inputs
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card custom-card">
            <div className="card-header">
              <h5 className="mb-0">Status &amp; preview</h5>
            </div>
            <div className="card-body">
              <h6>Active notices</h6>
              {activeNotices.length === 0 ? (
                <p className="text-muted">No active notices.</p>
              ) : (
                <ul className="list-group mb-3">
                  {activeNotices.map((n) => (
                    <li
                      key={n.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <span>
                        <span
                          className={`badge me-2 ${n.style === DeploymentNoticeStyle.Alert ? 'bg-danger' : 'bg-warning'}`}
                        >
                          {n.style === DeploymentNoticeStyle.Alert ? 'Alert' : 'Warning'}
                        </span>
                        {n.title}
                      </span>
                      {canManage && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger js-clear-active"
                          title="Clear"
                          onClick={() => setClearTarget(n.id)}
                        >
                          <i className="fa fa-times" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <h6>Live Preview</h6>
              <div
                className={`deployment-notice${styleNum === DeploymentNoticeStyle.Alert ? ' deployment-notice--alert' : ''} p-3 border rounded`}
              >
                <div className="deployment-notice__icon mb-1">
                  <i
                    className={`fa ${styleNum === DeploymentNoticeStyle.Alert ? 'fa-exclamation-circle' : 'fa-exclamation-triangle'}`}
                  />
                </div>
                {/* __text wraps title+body so the flex column shrinks (min-width:0) and the
                    message wraps instead of overflowing the box and scrolling the page. */}
                <div className="deployment-notice__text">
                  <div className="deployment-notice__title fw-semibold">{watched.title}</div>
                  <div
                    className="deployment-notice__body"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                </div>
              </div>

              <h6 className="mt-4">Saved templates</h6>
              {templates.length === 0 ? (
                <p className="text-muted mb-0">No templates saved yet.</p>
              ) : (
                <ul className="list-group">
                  {templates.map((t) => (
                    <li
                      key={t.id}
                      className="list-group-item d-flex justify-content-between align-items-center"
                    >
                      <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none js-edit-template"
                        onClick={() => fillFromTemplate(t.id)}
                      >
                        {t.name}
                      </button>
                      {canManage && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger js-delete-template"
                          title="Delete"
                          onClick={() => setDeleteTemplateTarget(t)}
                        >
                          <i className="fa fa-trash" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>

      <SaveTemplateModal
        show={showSaveTemplate}
        busy={createTemplateMut.isPending}
        onSubmit={handleSaveTemplate}
        onClose={() => setShowSaveTemplate(false)}
      />

      <ConfirmDialog
        show={clearTarget !== null}
        title="Clear notice"
        message="Clear this active notice?"
        confirmLabel="Clear"
        busy={clearMut.isPending}
        onConfirm={confirmClear}
        onCancel={() => setClearTarget(null)}
      />

      <ConfirmDialog
        show={deleteTemplateTarget !== null}
        title="Delete template"
        message={`Delete template "${deleteTemplateTarget?.name ?? ''}"?`}
        confirmLabel="Delete"
        busy={deleteTemplateMut.isPending}
        onConfirm={confirmDeleteTemplate}
        onCancel={() => setDeleteTemplateTarget(null)}
      />
    </div>
  );
}
